import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShoppingBag, X, Loader2, Clock, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import ModalShell from "@/components/ModalShell";
import MerchModal from "@/components/MerchModal";
import BackButton from "@/components/BackButton";

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function resolve(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Merchandise() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const isAdmin = user && user.role === "admin";

  const load = () =>
    Promise.all([api.get("/merch"), api.get("/merch-requests/me")]).then(([m, r]) => {
      setItems(m.data);
      setMine(r.data);
      setLoading(false);
    });

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-10 slide-up" data-testid="merch-page">
      <BackButton />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 flex items-center gap-2">
            <ShoppingBag className="h-3 w-3" /> Merchandise
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight">
            Apollo <span style={{ color: "#FBC90C" }}>Gear</span>
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Branded merch for retail partners and the Apollo team. Submit a request and the Apollo team will confirm and fulfill each order manually.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setAddOpen(true)}
            className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2"
            data-testid="merch-add-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Add Merch
          </button>
        )}
      </header>

      {loading ? (
        <div className="text-sm text-muted-foreground animate-pulse">Loading merchandise…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm py-24 text-center" data-testid="merch-empty">
          <ShoppingBag className="h-8 w-8 mx-auto text-muted-foreground" />
          <div className="mt-4 font-display text-2xl uppercase tracking-tight">No merchandise yet</div>
          <p className="text-sm text-muted-foreground mt-2">Check back soon — the Apollo team is stocking gear.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="merch-grid">
          {items.map((item) => (
            <MerchCard key={item.id} item={item} onSelect={() => setSelected(item)} />
          ))}
        </div>
      )}

      {mine.length > 0 && (
        <section className="border-t border-border pt-8">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Clock className="h-3 w-3" /> Your Requests
          </div>
          <div className="grid gap-2" data-testid="my-requests">
            {mine.map((r) => (
              <div key={r.id} className="border border-border rounded-sm bg-surface px-4 py-3 flex items-center justify-between text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">
                    {r.quantity} × {r.merch_name}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                    {new Date(r.created_at).toLocaleDateString()} · {money(r.merch_price * r.quantity)}
                  </div>
                </div>
                <StatusPill status={r.status} />
              </div>
            ))}
          </div>
        </section>
      )}

      <RequestModal
        item={selected}
        onClose={() => setSelected(null)}
        onSubmitted={() => {
          setSelected(null);
          load();
        }}
      />
      <MerchModal
        open={addOpen}
        item={null}
        onClose={() => setAddOpen(false)}
        onSaved={() => {
          setAddOpen(false);
          load();
        }}
      />
    </div>
  );
}

function MerchCard({ item, onSelect }) {
  const imgs = item.image_urls && item.image_urls.length ? item.image_urls : (item.image_url ? [item.image_url] : []);
  return (
    <button
      onClick={onSelect}
      className="text-left border border-border rounded-sm overflow-hidden bg-surface hover-lift group flex flex-col"
      data-testid={`merch-card-${item.id}`}
    >
      <div className="aspect-square bg-muted overflow-hidden relative">
        {imgs.length > 0 ? (
          <img
            src={resolve(imgs[0])}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-8 w-8" />
          </div>
        )}
        {imgs.length > 1 && (
          <span className="absolute bottom-2 left-2 px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-black/70 text-white rounded-sm" data-testid={`merch-card-count-${item.id}`}>
            {imgs.length} photos
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display text-lg uppercase font-semibold tracking-tight leading-tight">{item.name}</div>
        {item.description && (
          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</div>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between">
          <span className="font-mono text-sm font-semibold" style={{ color: "#FBC90C" }}>
            {money(item.price)}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Request →</span>
        </div>
      </div>
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "Pending", bg: "bg-surface-elev", fg: "text-foreground" },
    approved: { label: "Approved", bg: "", fg: "" },
    fulfilled: { label: "Fulfilled", bg: "", fg: "" },
    declined: { label: "Declined", bg: "", fg: "text-white" },
  };
  const style = map[status] || map.pending;
  const inline =
    status === "approved" ? { background: "#FBC90C", color: "#000" }
    : status === "fulfilled" ? { background: "#B02065", color: "#fff" }
    : status === "declined" ? { background: "#E15C2C", color: "#fff" }
    : {};
  return (
    <span
      className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-[0.3em] ${style.bg} ${style.fg}`}
      style={inline}
      data-testid={`status-${status}`}
    >
      {style.label}
    </span>
  );
}

function RequestModal({ item, onClose, onSubmitted }) {
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setQty(1);
    setNotes("");
    setImgIdx(0);
  }, [item?.id]);

  if (!item) return null;

  const imgs = item.image_urls && item.image_urls.length ? item.image_urls : (item.image_url ? [item.image_url] : []);
  const hasMulti = imgs.length > 1;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/merch/${item.id}/request`, { quantity: qty, notes });
      toast.success(`Request submitted for ${qty} × ${item.name}`);
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  return (
    <ModalShell open={!!item} onClose={onClose} maxWidth="max-w-lg" testId="merch-request-modal">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm"
        data-testid="merch-close"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Request Merch</div>
      <h2 className="font-display text-3xl uppercase font-bold tracking-tight">{item.name}</h2>

      {imgs.length > 0 && (
        <div className="mt-6">
          <div className="relative aspect-[4/3] bg-muted rounded-sm overflow-hidden border border-border">
            <img
              src={resolve(imgs[imgIdx])}
              alt={`${item.name} — ${imgIdx + 1}`}
              className="w-full h-full object-contain"
              data-testid="merch-request-main-image"
            />
            {hasMulti && (
              <>
                <button
                  type="button"
                  onClick={() => setImgIdx((i) => (i - 1 + imgs.length) % imgs.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:bg-black/80 transition"
                  data-testid="merch-request-prev"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setImgIdx((i) => (i + 1) % imgs.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:bg-black/80 transition"
                  data-testid="merch-request-next"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 backdrop-blur text-white font-mono text-[10px] uppercase tracking-[0.3em]" data-testid="merch-request-counter">
                  {imgIdx + 1} / {imgs.length}
                </div>
              </>
            )}
          </div>
          {hasMulti && (
            <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
              {imgs.map((u, i) => (
                <button
                  type="button"
                  key={u}
                  onClick={() => setImgIdx(i)}
                  className={`shrink-0 w-14 h-14 rounded-sm border overflow-hidden transition ${
                    i === imgIdx ? "border-foreground shadow-[0_0_0_2px_var(--foreground)]" : "border-border opacity-60 hover:opacity-100"
                  }`}
                  data-testid={`merch-request-thumb-${i}`}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={resolve(u)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 text-sm">
        {item.description && <p className="text-muted-foreground">{item.description}</p>}
        <div className="font-mono text-sm mt-2 font-semibold" style={{ color: "#FBC90C" }}>
          {money(item.price)} each
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-5">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">Quantity</label>
          <input
            type="number"
            min={1}
            max={999}
            value={qty}
            onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
            className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground"
            data-testid="merch-qty"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">Notes (optional)</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Size, color, delivery instructions…"
            className="w-full bg-transparent border border-border rounded-sm p-3 text-sm outline-none focus:border-foreground"
            data-testid="merch-notes"
          />
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Estimated total</div>
          <div className="font-display text-2xl font-bold tracking-tight" style={{ color: "#FBC90C" }}>
            {money(item.price * qty)}
          </div>
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-foreground text-background py-3 uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="merch-submit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Submit Request
        </button>
      </form>
    </ModalShell>
  );
}
